import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { BaseView } from "by/gamefactory/scene/viewable/BaseView";
import { ProcessTransactionPage } from "./pages/ProcessTransactionPage";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { CancellationToken } from "async/CancellationToken";
import { IDestroyOptions } from "pixi.js";
import { SuccessTransactionPage } from "./pages/SuccessTransactionPage";
import { FailedTransactionPage } from "./pages/FailedTransactionPage";
import { IFocusable } from "../../../layers/IFocusable";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { IMessagePage } from "./pages/IMessagePage";
import { DefaultMessagesResourcePackage } from "../resources/DefaultMessagesResourcePackage";
import { OperationResult } from "../OperationResult";
import { PaymentResult } from "../../../../../web3/PaymentResult";

export class DefaultMessagesView extends BaseView<OperationResult | PaymentResult> implements IFocusable {
    public readonly focusable: boolean = true;

    private readonly cancellationToken: CancellationToken;
    private readonly commonResources: CommonResourcesPackage;
    private readonly defaultMessagesResources: DefaultMessagesResourcePackage;
    private readonly operation: Promise<OperationResult | PaymentResult>;
    private readonly withProcessingPage: boolean;
    private readonly canvasScaler: ICanvasScaler;

    private fade: FadeScreen;
    private currentPage: IMessagePage;
    private closeButton: ButtonWithScaleAnimation;
    private operationResult: OperationResult | PaymentResult;

    private closing: boolean;

    public constructor(
        pcs: PromiseCompletionSource<OperationResult | PaymentResult>,
        cancellationToken: CancellationToken,
        commonResources: CommonResourcesPackage,
        defaultMessagesResources: DefaultMessagesResourcePackage,
        operation: Promise<OperationResult | PaymentResult>,
        withProcessingPage: boolean,
        canvasScaler: ICanvasScaler
    ) {
        super(pcs);
        this.cancellationToken = cancellationToken;
        this.commonResources = commonResources;
        this.defaultMessagesResources = defaultMessagesResources;
        this.operation = operation;
        this.withProcessingPage = withProcessingPage;
        this.canvasScaler = canvasScaler;
        this.operationResult = OperationResult.CANCELLED;
        this.closing = false;
        this.build();
        if (this.withProcessingPage) {
            this.showProcessPage();
        }
        this.cancellationToken?.onCanceled.add(this.onCancellationRequested, this);
        this.operation.then((result: OperationResult | PaymentResult) => this?.onOperationResult(result));
        this.closeButton.onUp.once(this.close, this);
    }

    public async start(): Promise<void> {
        await this.fade.fadeIn();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.cancellationToken?.onCanceled.remove(this.onCancellationRequested, this);
        super.destroy(options);
    }

    private build(): void {
        this.fade = new FadeScreen(0.5, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fade.eventMode = "static";
        this.addChild(this.fade);

        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.defaultView.width = this.closeButton.defaultView.height = 72;
        this.closeButton.position.set((this.canvasScaler.referenceWidth - this.closeButton.width) * 0.5, 1550);
        this.addChild(this.closeButton);
    }

    private onCancellationRequested(): void {
        this.close();
    }

    private async onOperationResult(result: OperationResult | PaymentResult): Promise<void> {
        if (this.closing) {
            return;
        }
        this.operationResult = result;
        if (result === OperationResult.CANCELLED || result === PaymentResult.CANCELLED) {
            this.close();
            return;
        }
        await this.currentPage?.hide();
        this.currentPage?.destroy();
        const page = this.currentPage = result === OperationResult.SUCCESS || result === PaymentResult.SUCCESS ?
            new SuccessTransactionPage(this.commonResources, this.canvasScaler)
            : new FailedTransactionPage(this.defaultMessagesResources, this.canvasScaler);
        this.addChild(page);
        await page.show();
    }

    private async close(): Promise<void> {
        if (this.cancellationToken != null && !this.cancellationToken.isCancellationRequested) {
            this.cancellationToken.cancel();
        }
        this.closing = true;
        await Promise.all([this.fade.fadeOut(), this.currentPage.hide()]);
        this.operationControls.complete(this.operationResult);
    }

    private async showProcessPage(): Promise<void> {
        const page = this.currentPage = new ProcessTransactionPage(this.commonResources, this.canvasScaler);
        this.addChild(page);
        await Promise.all([page.show()]);
    }

}
